// src/features/dashboard/components/ChartsSection.tsx

import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import {
  TrendingUp,
  Lock,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Line,
} from 'recharts';
import { useTranslate } from 'react-admin';
import { DashboardSummaryResponse, UsageTrendsResponse } from '../types';

interface ChartsSectionProps {
  usageTrends: UsageTrendsResponse | null;
  dashboardSummary: DashboardSummaryResponse;
}

const ChartsSection: React.FC<ChartsSectionProps> = ({ usageTrends, dashboardSummary }) => {
  const translate = useTranslate();

  // Prepare weekly trend data
  const weeklyTrendData = dashboardSummary.weeklyTrend || [
    { day: 'Mon', assignments: 0, occupancy: 66 },
    { day: 'Tue', assignments: 0, occupancy: 67 },
    { day: 'Wed', assignments: 0, occupancy: 66 },
    { day: 'Thu', assignments: 0, occupancy: 67 },
    { day: 'Fri', assignments: 0, occupancy: 66 },
    { day: 'Sat', assignments: 0, occupancy: 67 },
    { day: 'Sun', assignments: 0, occupancy: 66.7 }
  ];

  // Prepare locker status data for pie chart
  const lockerStatusData = [
    { 
      name: 'Available', 
      value: dashboardSummary.availableLockers || 0,
      percentage: dashboardSummary.totalLockers > 0 
        ? ((dashboardSummary.availableLockers / dashboardSummary.totalLockers) * 100).toFixed(0)
        : 0,
      color: '#4caf50' 
    },
    { 
      name: 'Occupied', 
      value: (dashboardSummary.totalLockers - dashboardSummary.availableLockers) || 0,
      percentage: dashboardSummary.totalLockers > 0 
        ? (((dashboardSummary.totalLockers - dashboardSummary.availableLockers) / dashboardSummary.totalLockers) * 100).toFixed(0)
        : 0,
      color: '#2196f3' 
    },
    { 
      name: 'Blocked', 
      value: dashboardSummary.blockedLockers || 0,
      percentage: 0,
      color: '#ff9800' 
    },
    { 
      name: 'Maintenance', 
      value: 0,
      percentage: 0,
      color: '#f44336' 
    },
    { 
      name: 'Reserved', 
      value: 0,
      percentage: 0,
      color: '#9c27b0' 
    }
  ].filter(item => item.value > 0); // Only show non-zero values

  // Custom label for pie chart
  const renderCustomLabel = (entry: any) => {
    return `${entry.name}: ${entry.percentage}%`;
  };

  return (
    <Grid container spacing={2.5} sx={{ mb: 3 }}>
      {/* Weekly Usage Trend Chart */}
      <Grid item xs={12} md={8}>
        <Card sx={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TrendingUp sx={{ fontSize: 20, color: '#666' }} />
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                {translate('resources.dashboard.weekly_usage_trend')}
              </Typography>
            </Box>
            
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={weeklyTrendData}>
                <defs>
                  <linearGradient id="colorOccupancy" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8bc34a" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8bc34a" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="day" 
                  tick={{ fontSize: 12 }}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e0e0e0',
                    borderRadius: 4
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="occupancy"
                  stroke="#8bc34a"
                  fillOpacity={1}
                  fill="url(#colorOccupancy)"
                  strokeWidth={2}
                  name={translate('resources.dashboard.occupancy_percent')}
                />
                <Line
                  type="monotone"
                  dataKey="assignments"
                  stroke="#2196f3"
                  strokeWidth={2}
                  dot={{ fill: '#2196f3', r: 4 }}
                  name={translate('resources.dashboard.assignments_label')}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Locker Status Pie Chart */}
      <Grid item xs={12} md={4}>
        <Card sx={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Lock sx={{ fontSize: 20, color: '#666' }} />
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                {translate('resources.dashboard.locker_status')}
              </Typography>
            </Box>
            
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={lockerStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {lockerStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconSize={10}
                  formatter={(value) => (
                    <span style={{ fontSize: '12px', color: '#666' }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ChartsSection;